import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { PillButton } from '../PillButton.tsx';
import { useFestivalData } from '../../lib/festivalData.ts';
import { usePageHasHero } from '../../lib/pageHasHero.ts';
import { getArtistGroups } from '../../application/festivalSelectors.ts';
import { SITE_NAME } from '../../../shared/meta.mjs';
import {
  desktopNameSize, festivalYear, isFrontPageFormat, mobileNameSize, tickerLoop, tickerSeconds, tickerWords,
} from '../../domain/programTop.ts';
import type { Artist, ProgramTopSection } from '../../lib/types.ts';

/**
 * "Program {år}" at the top of the front page (Program 2027). Placed as the page's first section in
 * the phases whose hero is "Ingen hero", so it carries the page's `<h1>` — an `<h2>` when the page
 * has a hero after all.
 *
 * A dumb consumer of program.json, like the current-year line-up strip: program.json leaves an
 * artist out until their announcement time, so the top changes by itself when it passes. The first
 * announced artist (by the order set in Arranger artister) fills it; before anyone is announced it
 * shows a big "?". No dates for the next name and no numbers, anywhere.
 *
 * The colors are fixed (the canvas "Artist-annoncering 2027"); the section's theme is not used.
 */
export function CmsProgramTop({ eyebrow, emptyText, barText, primaryCta, secondaryCta }: ProgramTopSection) {
  const { data, loading, error } = useFestivalData();
  const hasHero = usePageHasHero();

  if (error) return null;
  if (loading || !data) {
    // Holds the top's height while program.json loads, so the page below does not jump.
    return <div aria-hidden="true" className="bg-mf-ink"><div className="w-full aspect-[1225/720] md:aspect-[1707/720] bg-mf-green" /></div>;
  }

  const year = festivalYear(data.program.startDate);
  const artists = getArtistGroups(data).artists;
  const artist = artists[0] ?? null;
  const Heading = hasHero ? 'h2' : 'h1';
  const nameText = artist ? artist.name : year;

  const words = tickerLoop(tickerWords(artists, year));
  const sizes = {
    '--mf-pt-name-m': mobileNameSize(nameText),
    '--mf-pt-name-d': desktopNameSize(nameText),
    '--mf-pt-ticker-m': `${tickerSeconds(words, 17, 14, 24)}s`,
    '--mf-pt-ticker-d': `${tickerSeconds(words, 24, 20, 36)}s`,
  } as CSSProperties;

  const ctas = [primaryCta, secondaryCta].filter(c => !!c?.label && !!c.link);

  return (
    <section aria-label={`Navne til ${SITE_NAME} ${year}`} className="mf-programtop bg-mf-ink text-mf-yellow" style={sizes}>
      <Ticker words={words} />

      <div className="mf-programtop-top relative overflow-hidden bg-[#333335]">
        {artist ? <ArtistImage artist={artist} /> : <QuestionMark />}

        <Heading className="mf-programtop-name absolute z-[1] left-0 top-0 w-[16.4%] md:left-auto md:right-0 md:bottom-0 md:w-[11.5%] md:px-[clamp(8px,1.4vw,20px)] m-0 flex items-center justify-center font-display font-bold uppercase leading-[0.8] tracking-[-0.03em] text-center whitespace-nowrap text-mf-yellow [writing-mode:vertical-rl] rotate-180">
          {artist ? (
            <Link to={artistPath(artist)} className="hover:text-mf-orange">
              <span className="sr-only">{SITE_NAME} – </span>{artist.name}
            </Link>
          ) : (
            <><span className="sr-only">{SITE_NAME} – første navn til </span>{year}</>
          )}
        </Heading>

        <div className="bg-mf-green px-4 pt-3 pb-3.5 md:bg-transparent md:absolute md:left-0 md:top-0 md:bottom-0 md:w-[28.5%] md:flex md:flex-col md:justify-end md:gap-3.5 md:pt-7 md:pr-4 md:pb-[clamp(24px,3vw,44px)] md:pl-[clamp(16px,2.5vw,40px)]">
          {eyebrow && (
            <p className="m-0 text-[11px] md:text-[13px] tracking-[0.14em] md:tracking-[0.16em] font-bold uppercase text-mf-ice">{eyebrow}</p>
          )}
          {artist ? (
            artist.heroText && (
              <p className="mt-1.5 md:mt-0 text-base md:text-[clamp(16px,1.4vw,20px)] leading-[1.35] md:leading-[1.4] max-w-[440px] text-white">{artist.heroText}</p>
            )
          ) : (
            emptyText && (
              <p className="mt-1.5 md:mt-0 text-base md:text-[clamp(18px,1.6vw,24px)] leading-[1.35] max-w-[460px] text-white">{emptyText}</p>
            )
          )}
          <p className="mt-1.5 md:mt-0 text-[13px] md:text-sm md:tracking-[0.04em] text-mf-ice">Første weekend i september {year}</p>
          {artist && (
            <p className="hidden md:block m-0 text-[15px]">
              <Link to={artistPath(artist)} className="text-mf-yellow underline hover:text-mf-orange">Læs om {artist.name} →</Link>
            </p>
          )}
        </div>
      </div>

      {(barText || ctas.length > 0) && (
        <div className="bg-mf-yellow text-mf-green">
          <div className="max-w-[1440px] mx-auto px-4 md:px-[clamp(16px,2.5vw,40px)] py-3 md:py-[18px] flex flex-col md:flex-row md:flex-wrap md:items-center gap-2.5 md:gap-x-8 md:gap-y-4">
            {barText && <p className="m-0 md:grow text-[15px] md:text-xl font-bold md:tracking-[-0.02em]">{barText}</p>}
            {ctas.length > 0 && (
              <div className="flex flex-wrap gap-2.5 md:gap-3">
                {ctas.map((cta, i) => (
                  <PillButton key={i} as="a" href={cta!.link} variant={i === 0 ? 'coral' : 'outline'}>{cta!.label}</PillButton>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/** The artist's place in the program (the artist page is the program's anchor for them). */
function artistPath(artist: Artist): string {
  return `/program#artist-${artist.id}`;
}

/** The front-page image, full width in its own format on desktop and cropped to 1225:720 on a
 *  phone. One in another format, or none, still gets the top's frame: cropped to it, or the
 *  plain green behind the name. */
function ArtistImage({ artist }: { artist: Artist }) {
  const image = artist.images?.hero;
  if (!image) return <div className="w-full aspect-[1225/720] md:aspect-[1707/720] bg-mf-green" />;
  const ownFormat = isFrontPageFormat(image);
  return (
    <img
      src={image.url}
      width={image.width}
      height={image.height}
      alt={`${artist.name}, pressefoto`}
      fetchPriority="high"
      className={
        'block w-full aspect-[1225/720] object-cover object-[59%_50%] ' +
        (ownFormat ? 'md:aspect-auto md:h-auto md:object-fill' : 'md:aspect-[1707/720] md:object-center')
      }
    />
  );
}

/** Before the first name: a big question mark where the image will be. */
function QuestionMark() {
  return (
    <div aria-hidden="true" className="relative w-full aspect-[1225/720] md:aspect-[1707/720] bg-mf-green">
      <div className="absolute inset-y-0 left-[16.4%] w-[83.6%] md:left-[28.5%] md:w-[60%] flex items-center justify-center overflow-hidden">
        <span className="mf-programtop-q font-display font-bold leading-none tracking-[-0.04em] text-mf-yellow">?</span>
      </div>
    </div>
  );
}

/** The coral name band. Decorative: the names are on the page as text. */
function Ticker({ words }: { words: readonly string[] }) {
  return (
    <div aria-hidden="true" className="bg-mf-coral text-mf-green overflow-hidden whitespace-nowrap border-b-2 border-mf-green">
      <div className="mf-programtop-track inline-flex gap-6 md:gap-9 py-[9px] md:py-3 text-[17px] md:text-2xl font-bold md:tracking-[-0.01em] uppercase">
        {[...words, ...words].map((w, i) => (
          <span key={i} className="inline-flex items-center gap-6 md:gap-9">
            {w}
            <svg className="w-3.5 h-3.5 md:w-5 md:h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l2.6 7.4H22l-6 4.6 2.3 7.4L12 16.8 5.7 21.4 8 14 2 9.4h7.4z" />
            </svg>
          </span>
        ))}
      </div>
    </div>
  );
}
